
import { PrayerRequest, PrayerType } from '@/types';
import { formatShortName, publicLocation } from '@/lib/format';
import { cn } from '@/lib/utils';
import { Button, Card } from '@/components/ui';

export interface PrayerCardProps {
  prayer: PrayerRequest;
  onNext?: () => void;
  isLoading?: boolean;
  isNextDisabled?: boolean;
  nextLabel?: string;
  /** Show category badge (default true). Set false e.g. on /pray viewer. */
  showCategory?: boolean;
  /** Seconds remaining in cooldown (for pacing enforcement) */
  cooldownSecondsRemaining?: number;
  /** True if we should show a gentle reminder about taking time to pray */
  shouldShowPacingReminder?: boolean;
}

/**
 * Category color mapping for visual distinction
 */
const categoryColors: Record<string, string> = {
  healing: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  finances: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  family: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  spiritual: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  relationships: 'bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-300',
  career: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  health: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300',
  guidance: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
  other: 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-300',
  default: 'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300',
};

/**
 * Get the color classes for a category
 */
const defaultCategoryColor = 'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300';

function getCategoryColor(category: string | null): string {
  if (!category) return defaultCategoryColor;
  const normalizedCategory = category.toLowerCase();
  return categoryColors[normalizedCategory] ?? defaultCategoryColor;
}

/**
 * PrayerCard Component
 * Displays a single prayer request in a beautifully styled card
 * with category badge, prayer body, author info, and navigation
 */
export function PrayerCard({ prayer, onNext, isLoading, isNextDisabled, nextLabel, showCategory = true, cooldownSecondsRemaining = 0, shouldShowPacingReminder = false }: PrayerCardProps) {
  const isPraise = prayer.type === PrayerType.PRAISE;
  const categoryColor = getCategoryColor(prayer.category);
  const isDisabled = Boolean(isLoading || isNextDisabled || cooldownSecondsRemaining > 0);
  const hasCooldown = cooldownSecondsRemaining > 0;
  const visibleLocation = publicLocation(prayer.location, prayer.isAnonymous);
  // Show "Please wait..." when cooldown is active, otherwise use provided label or default
  const label = hasCooldown ? 'Please wait...' : (nextLabel || 'Next Prayer');

  return (
    <Card
      variant="elevated"
      padding="none"
      className={cn(
        "p-6 sm:p-8 max-w-md w-full mx-auto",
        "transform transition-all duration-300 ease-out",
        "animate-fade-in"
      )}
      role="article"
      aria-label={`Prayer request from ${prayer.isAnonymous ? 'Anonymous' : (formatShortName(prayer.firstName, prayer.lastName) || 'Anonymous')}`}
    >
      {/* Category / type badges (category hidden when showCategory=false) */}
      <div className="mb-4 flex flex-wrap gap-2">
        {showCategory && prayer.category && (
          <span 
            className={cn(
              "inline-block px-3 py-1 text-sm font-medium rounded-full",
              "transition-colors duration-200",
              categoryColor
            )}
          >
            {prayer.category}
          </span>
        )}
        {isPraise && (
          <span 
            className={cn(
              "inline-block px-3 py-1 text-sm font-medium rounded-full",
              "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300"
            )}
          >
            <span aria-hidden="true">✨</span> Answered Prayer
          </span>
        )}
      </div>
      
      {/* Prayer body */}
      <div className="mb-6">
        <p 
          className={cn(
            "text-lg sm:text-xl text-slate-700 dark:text-slate-200",
            "leading-relaxed font-medium"
          )}
        >
          {prayer.body}
        </p>
      </div>
      
      {/* Author info */}
      <div 
        className={cn(
          "text-sm text-slate-500 dark:text-slate-400",
          "mb-6 pb-6 border-b border-slate-200 dark:border-slate-700"
        )}
      >
        <span className="font-medium">
          {prayer.isAnonymous ? 'Anonymous' : (formatShortName(prayer.firstName, prayer.lastName) || 'Anonymous')}
        </span>
        {visibleLocation && (
          <span className="before:content-['•'] before:mx-2">
            {visibleLocation}
          </span>
        )}
      </div>
      
      {/* Gentle reminder message (shown during grace period) */}
      {shouldShowPacingReminder && !hasCooldown && (
        <div className={cn(
          "mb-4 p-3 rounded-lg",
          "bg-blue-50 dark:bg-blue-900/20",
          "border border-blue-200 dark:border-blue-800",
          "text-sm text-blue-700 dark:text-blue-300",
          "text-center",
          "animate-fade-in"
        )}>
          <p className="font-medium">
            💙 Please take time to pray over each request
          </p>
          <p className="text-xs mt-1 opacity-90">
            These are real prayer needs from your community
          </p>
        </div>
      )}

      {/* Next button */}
      <Button
        onClick={onNext}
        disabled={isDisabled}
        isLoading={isLoading}
        fullWidth
        size="lg"
        className={cn(
          "rounded-xl",
          hasCooldown && "!bg-slate-400 hover:!bg-slate-400"
        )}
        aria-label={
          isLoading
            ? 'Loading next prayer'
            : hasCooldown
              ? 'Please wait before continuing'
              : isNextDisabled
                ? 'Next prayer disabled'
                : 'Go to next prayer'
        }
        rightIcon={
          !isLoading && !hasCooldown ? (
            <svg 
              className="w-5 h-5" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M13 7l5 5m0 0l-5 5m5-5H6" 
              />
            </svg>
          ) : undefined
        }
      >
        {!isLoading && label}
      </Button>
    </Card>
  );
}

export default PrayerCard;
